import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AuctionRoomHeader from '../../components/specific/auction/AuctionRoomHeader';
import BidPanel from '../../components/specific/auction/BidPanel';
import BidHistory from '../../components/specific/auction/BidHistory';
import auctionService from '../../services/auctionService';
import { useAuctionSocket } from '../../hooks/useAuctionSocket';

const MAX_BID_HISTORY_ITEMS = 200;

export default function AuctionRoom() {
    const { sessionId } = useParams();
    const navigate = useNavigate();

    const handleLeaveRoom = () => {
        navigate('/manager/dashboard/live-bid-sessions');
    };

    const [session, setSession] = useState(null);
    const [highest, setHighest] = useState(null);
    const [bids, setBids] = useState([]);
    const [participantCount, setParticipantCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [socketConnecting, setSocketConnecting] = useState(false);

    // Memoize callback functions to prevent unnecessary re-registrations
    const handleJoined = useCallback((payload) => {
        console.log('[AuctionRoom] joined →', payload);
        // Restore state from socket event (important for reconnection)
        if (payload.history) {
            setBids(Array.isArray(payload.history) ? payload.history.slice(0, MAX_BID_HISTORY_ITEMS) : []);
        }
        if (payload.highest) {
            setHighest(payload.highest);
        }
        if (payload.participants !== undefined) {
            setParticipantCount(Array.isArray(payload.participants) ? payload.participants.length : payload.participants);
        }
        // Clear socket connecting state when we receive joined event
        setSocketConnecting(false);
    }, []);

    const handleNewHighestBid = useCallback((payload) => {
        console.log('[socket] new_highest_bid →', payload);
        setHighest(payload);
        setBids(prev => [payload, ...prev].slice(0, MAX_BID_HISTORY_ITEMS));
    }, []);

    const handleBiddingEnded = useCallback((payload) => {
        console.log('[socket] bidding_ended →', payload);
        setSession(s => s ? { ...s, status: 'completed' } : null);
        alert(`Auction ended! Winner: ${payload.winner || 'No one'}`);
    }, []);

    const handleParticipantJoined = useCallback(() => {
        setParticipantCount(prev => prev + 1);
    }, []);

    const handleParticipantLeft = useCallback(() => {
        setParticipantCount(prev => Math.max(0, prev - 1));
    }, []);

    const handleError = useCallback((payload) => {
        alert(payload.message || 'Socket error');
    }, []);

    const { joinBiddingRoom, placeBid, leaveBiddingRoom, isConnected } = useAuctionSocket({
        onJoined: handleJoined,
        onNewHighestBid: handleNewHighestBid,
        onBiddingEnded: handleBiddingEnded,
        onParticipantJoined: handleParticipantJoined,
        onParticipantLeft: handleParticipantLeft,
        onError: handleError
    });

    // Fetch session snapshot once when sessionId changes
    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                const { data } = await auctionService.getAuctionSession(sessionId);
                if (cancelled) return;

                setSession(data);
                // Set initial state from API (will be updated by socket events)
                setHighest(data.current_highest || data.current_highest_bid || null);
                setBids(Array.isArray(data.bids) ? data.bids.slice(0, MAX_BID_HISTORY_ITEMS) : []);
                setParticipantCount(Array.isArray(data.participants) ? data.participants.length : 0);
            } catch (err) {
                console.error('Failed to load auction session', err);
                if (!cancelled) {
                    // Show error state
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    // Join room safely after socket connects and session is loaded
    useEffect(() => {
        if (!sessionId || !isConnected) return; // Wait for socket to connect

        console.log('[AuctionRoom] Socket connected, joining room:', sessionId);
        setSocketConnecting(true);

        // Join room (idempotent - won't join twice)
        const joined = joinBiddingRoom(sessionId);

        if (!joined) {
            console.warn('[AuctionRoom] Failed to join room');
            setSocketConnecting(false);
        }
        // Note: setSocketConnecting(false) will be called in handleJoined callback

        return () => {
            console.log('[AuctionRoom] Cleaning up, leaving room:', sessionId);
            leaveBiddingRoom(sessionId);
            setSocketConnecting(false);
        };
    }, [isConnected, sessionId, joinBiddingRoom, leaveBiddingRoom]); // Simplified dependencies

    const handlePlaceBid = (amount) => placeBid(sessionId, amount);

    // Show loading state while fetching session or connecting to socket
    if (loading) {
        return <DashboardLayout>Loading Auction Room…</DashboardLayout>;
    }

    if (!session) {
        return <DashboardLayout>Oh! No! Session not found.</DashboardLayout>;
    }

    // Show connecting state if socket is not connected yet
    if (socketConnecting && !isConnected) {
        return (
            <DashboardLayout>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <p>Connecting to auction room...</p>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div style={{ padding: '1rem' }}>
                <AuctionRoomHeader
                    highest={highest}
                    session={session}
                    participantCount={participantCount}
                    onLeave={handleLeaveRoom}
                />

                <div className="auction-room-grid">
                    <div className="main-auction-area">
                        <div
                            className="artwork-preview-placeholder"
                            style={{
                                backgroundColor: '#eee',
                                height: '300px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '8px',
                                marginBottom: '1rem',
                            }}
                        >
                            Artwork Preview Placeholder
                        </div>

                        {session.status === 'active' ? (
                            <BidPanel
                                sessionId={sessionId}
                                currentHighest={highest?.amount || session.starting_price}
                                minIncrement={session.min_bid_increment}
                                onPlaceBid={handlePlaceBid}
                            />
                        ) : (
                            <div
                                style={{
                                    padding: '1rem',
                                    background: '#f8d7da',
                                    color: '#721c24',
                                    borderRadius: '4px',
                                }}
                            >
                                This auction is not active.
                            </div>
                        )}
                    </div>

                    <div className="side-auction-area">
                        <BidHistory bids={bids} />
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}
